"use client";
const countryList = [
  {
    en: "AE",
    zh: "阿联酋",
  },
  {
    en: "AU",
    zh: "澳大利亚",
  },
  {
    en: "BR",
    zh: "巴西",
  },
  {
    en: "CA",
    zh: "加拿大",
  },
  {
    en: "CN",
    zh: "中国",
  },
  {
    en: "DE",
    zh: "德国",
  },
  {
    en: "FR",
    zh: "法国",
  },
  {
    en: "HK",
    zh: "香港",
  },
  {
    en: "ID",
    zh: "印度尼西亚",
  },
  {
    en: "IN",
    zh: "印度",
  },
  {
    en: "JP",
    zh: "日本",
  },
  {
    en: "KR",
    zh: "韩国",
  },
  {
    en: "MY",
    zh: "马来西亚",
  },
  {
    en: "PH",
    zh: "菲律宾",
  },
  {
    en: "RU",
    zh: "俄罗斯",
  },
  {
    en: "SE",
    zh: "瑞典",
  },
  {
    en: "SG",
    zh: "新加坡",
  },
  {
    en: "TH",
    zh: "泰国",
  },
  {
    en: "TW",
    zh: "台湾",
  },
  {
    en: "UK",
    zh: "英国",
  },
  {
    en: "US",
    zh: "美国",
  },
  {
    en: "VN",
    zh: "越南",
  },
];

import { useState, useRef, useEffect } from "react";

export const Region = () => {
  const [currentPage, setCurrentPage] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [currentX, setCurrentX] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  // 第一页：3行 × 4列 = 12个国家
  const itemsPerPage = 12;
  const firstPageItems = countryList.slice(0, itemsPerPage);
  const secondPageItems = countryList.slice(itemsPerPage);

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setStartX(e.clientX);
    setCurrentX(e.clientX);
    e.preventDefault(); // 阻止默认行为，避免文本选择
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;
      setCurrentX(e.clientX);
    };

    const handleMouseUp = () => {
      if (!isDragging) return;

      const diff = startX - currentX;
      const containerWidth = containerRef.current?.clientWidth || 0;
      const threshold = containerWidth * 0.15; // 拖拽超过容器宽度的15%才切换页面

      if (Math.abs(diff) > threshold) {
        if (diff > 0 && currentPage < 1) {
          // 向右拖拽，切换到下一页
          setCurrentPage(currentPage + 1);
        } else if (diff < 0 && currentPage > 0) {
          // 向左拖拽，切换到上一页
          setCurrentPage(currentPage - 1);
        }
      }

      setIsDragging(false);
      setStartX(0);
      setCurrentX(0);
    };

    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, startX, currentX, currentPage]);

  // 计算拖拽偏移量（像素）
  const dragOffset = isDragging ? startX - currentX : 0;
  const containerWidth = containerRef.current?.clientWidth || 0;

  // 限制拖拽范围
  let normalizedDragOffset = dragOffset;
  if (currentPage === 0 && dragOffset < 0) {
    // 在第一页，不能向右拖拽
    normalizedDragOffset = Math.max(dragOffset, -containerWidth * 0.3);
  } else if (currentPage === 1 && dragOffset > 0) {
    // 在最后一页，不能向左拖拽
    normalizedDragOffset = Math.min(dragOffset, containerWidth * 0.3);
  }

  const renderPage = (items: typeof countryList) => {
    const rows = Math.ceil(items.length / 4);
    return (
      <div className="w-full flex-shrink-0">
        {Array.from({ length: rows }, (_, rowIndex) => (
          <div key={rowIndex} className="grid grid-cols-4 gap-10 mb-10">
            {items.slice(rowIndex * 4, (rowIndex + 1) * 4).map((item) => (
              <div
                key={item.en}
                className="w-[229px] h-[82px] px-6 py-4 bg-white rounded-xl shadow-[0px_4px_30px_0px_rgba(0,0,0,0.08)] inline-flex justify-start items-center gap-5"
              >
                <img
                  className="w-15 h-15"
                  src={`/imgs/country/${item.en}.webp`}
                  alt={item.zh}
                />
                <div className="w-24 inline-flex flex-col justify-start items-start gap-[3px]">
                  <div className="justify-start text-zinc-800 text-xl font-medium font-['Alibaba_PuHuiTi_3.0']">
                    {item.zh}
                  </div>
                </div>
              </div>
            ))}
            {/* 如果最后一行不足4个，用空白div填充 */}
            {items.slice(rowIndex * 4, (rowIndex + 1) * 4).length < 4 &&
              Array.from(
                {
                  length:
                    4 - items.slice(rowIndex * 4, (rowIndex + 1) * 4).length,
                },
                (_, i) => <div key={`empty-${i}`} className="w-[229px]"></div>
              )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="flex justify-center bg-gradient-to-b from-white via-purple-50 to-white">
      <div className="px-[15rem] py-[50px] w-[1920px]">
        <div className="text-[#333] text-center font-['Alibaba_PuHuiTi_3.0'] text-[45px] font-bold leading-[120%] tracking-[4.5px] mb-[100px]">
          TikTok可购买的区域
        </div>

        <div className="w-full mt-[50px] relative">
          {/* 滑动容器 */}
          <div
            ref={containerRef}
            className="overflow-hidden cursor-grab active:cursor-grabbing select-none"
            onMouseDown={handleMouseDown}
            style={{ userSelect: "none" }}
          >
            <div
              className="flex"
              style={{
                transform: `translateX(calc(-${
                  currentPage * 100
                }% + ${normalizedDragOffset}px))`,
                transition: isDragging ? "none" : "transform 0.3s ease-in-out",
                cursor: isDragging ? "grabbing" : "grab",
              }}
            >
              {/* 第一页 */}
              {renderPage(firstPageItems)}
              {/* 第二页 */}
              {renderPage(secondPageItems)}
            </div>
          </div>

          {/* 分页指示器 */}
          <div className="flex justify-center gap-3 mt-10">
            <button
              onClick={() => setCurrentPage(0)}
              className="transition-all"
              style={{
                width: currentPage === 0 ? "62px" : "14px",
                height: "14px",
                background: currentPage === 0 ? "#AAD5FF" : "#C2E1FF",
                borderRadius: "30px",
              }}
              aria-label="第一页"
            />
            <button
              onClick={() => setCurrentPage(1)}
              className="transition-all"
              style={{
                width: currentPage === 1 ? "62px" : "14px",
                height: "14px",
                background: currentPage === 1 ? "#AAD5FF" : "#C2E1FF",
                borderRadius: "30px",
              }}
              aria-label="第二页"
            />
          </div>
        </div>
      </div>
    </div>
  );
};
